import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useInView, useScroll, useTransform } from 'framer-motion';
import {
  Cpu, Receipt,
  BarChart3, ChevronDown, Check,
  ArrowLeft, Phone, Mail, MapPin,
  Lock, Server,
  Clock, Quote,
  Linkedin, Twitter,
  Zap, ListChecks, CreditCard, BrainCircuit, Network,
  Globe, Search, Languages, Smartphone, MousePointerClick, CalendarCheck, Sparkles, Layout,
} from 'lucide-react';
import LandingNavbar from '../../components/LandingNavbar';

/* ------------------------------------------------------------------ */
/*  DATA                                                               */
/* ------------------------------------------------------------------ */
const FEATURES = [
  { icon: Zap, title: 'تسجيل فوري بدون انتظار', desc: 'سجّل واختر باقتك وابدأ خلال دقائق. بدون نماذج، بدون فريق مبيعات، بدون انتظار أحد يتواصل معك.', tag: 'حصري' },
  { icon: ListChecks, title: '+201 تحليل جاهز', desc: 'قوالب تحاليل مُعدّة مسبقًا بالمعايير المرجعية والوحدات الدولية. فعّل اللي تحتاجه وابدأ — بدون إدخال يدوي يأخذ أسابيع.', tag: 'حصري' },
  { icon: CreditCard, title: 'أسعار واضحة وشفافة', desc: 'شوف الأسعار مباشرة، اختر باقتك، بدون رسوم مخفية أو عقود إجبارية. ألغِ في أي وقت.' },
  { icon: Cpu, title: 'ربط الأجهزة', desc: 'ربط تلقائي مع أجهزة التحليل عبر HL7 و ASTM — النتائج تنتقل مباشرة بدون إدخال يدوي.', tag: 'متقدم' },
  { icon: Receipt, title: 'فوترة ZATCA', desc: 'فوترة إلكترونية متوافقة مع المرحلة الثانية تلقائيًا — بدون تدخل يدوي أو قلق من الغرامات.' },
  { icon: Network, title: 'بوابة المختبرات المتعاقدة', desc: 'إدارة التحويلات والعمل المشترك مع المختبرات الشريكة من بوابة واحدة مخصصة.' },
  { icon: BarChart3, title: 'تقارير وتحليلات', desc: 'تقارير أداء ودخل تفصيلية تساعدك تاخذ قرارات أفضل مبنية على أرقام حقيقية.' },
];

const STEPS = [
  { num: '01', title: 'اختر باقتك', desc: 'الأسعار واضحة. اختر الباقة المناسبة وسجّل بنفسك — بدون انتظار فريق مبيعات أو مفاوضات.' },
  { num: '02', title: 'فعّل القوالب', desc: 'اختر التحاليل اللي تحتاجها من +201 قالب جاهز بالمعايير والوحدات. بدون إدخال يدوي.' },
  { num: '03', title: 'ابدأ العمل', desc: 'نظامك جاهز. ابدأ استقبال الحجوزات وإصدار النتائج والفواتير من اليوم.' },
];

const FAQS = [
  { q: 'هل أحتاج أدخل كل التحاليل يدويًا؟', a: 'أبدًا. النظام يجي مع +201 قالب تحليل جاهز بالمعايير المرجعية والوحدات الدولية. بس فعّل اللي تحتاجه وابدأ — بدون إدخال يدوي يأخذ أسابيع مثل الأنظمة الثانية.' },
  { q: 'كم يأخذ إعداد النظام؟', a: 'دقائق مو أسابيع. سجّل، اختر باقتك، فعّل القوالب، وابدأ. ما تحتاج تنتظر فريق مبيعات يتواصل معك أو فني يركّب لك النظام.' },
  { q: 'هل الأسعار اللي في الموقع نهائية؟', a: 'نعم، 100%. الأسعار واضحة وشفافة — اللي تشوفه هو اللي تدفعه. بدون رسوم مخفية، بدون تكاليف إعداد، بدون مفاجآت.' },
  { q: 'هل بيانات المرضى آمنة مع خاصية الـ AI؟', a: 'نعم، الذكاء الاصطناعي يشتغل بنموذج محلي — بيانات المرضى ما تطلع من سيرفراتك أبدًا. ما نرسل أي بيانات لخدمات AI خارجية مثل OpenAI أو غيرها. خصوصية كاملة.' },
  { q: 'هل أحصل على موقع إلكتروني فعلاً؟ كم يكلّف؟', a: 'الموقع متاح مع الباقة المتقدمة والاحترافية بدون رسوم منفصلة — يعني بدلاً من تدفع لمطوّر آلاف الريالات، تحصل على موقع متكامل ضمن اشتراكك. تختار من قوالب احترافية متعددة، نطاق فرعي مجاني (lab.sites.listechs.com) أو ربط نطاقك الخاص (lab.com)، محرر مرئي بالنقر، SEO جاهز (sitemap + schema)، دعم ثنائي اللغة، وربط مباشر بنظام الحجوزات.' },
  { q: 'هل يدعم النظام تعدد الفروع؟', a: 'نعم، لوحة تحكم مركزية واحدة لجميع فروعك. كل فرع له إعداداته وصلاحياته الخاصة، وتقارير موحدة تعطيك الصورة الكاملة.' },
  { q: 'هل أقدر ألغي الاشتراك بأي وقت؟', a: 'طبعًا. بدون عقود طويلة وبدون شروط جزائية. تقدر تلغي في أي وقت من إعدادات حسابك. بياناتك تبقى متاحة لك للتصدير.' },
];

/* ------------------------------------------------------------------ */
/*  SHARED COMPONENTS                                                  */
/* ------------------------------------------------------------------ */
function SectionLabel({ text }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <span className="block w-8 h-[2px] bg-[#0F6E56]" />
      <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-[#8A8780]">
        {text}
      </span>
    </div>
  );
}

function RevealSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 32 }}
      transition={{ duration: 0.6, delay, ease: [0.25, 0.1, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}



/* ------------------------------------------------------------------ */
/*  MAIN COMPONENT                                                     */
/* ------------------------------------------------------------------ */
export default function LandingPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroParallax = useTransform(scrollYProgress, [0, 1], [0, -60]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div dir="rtl" className="landing-page bg-[#FDFCF9] text-[#1A1A18]" style={{ fontFamily: "'Readex Pro', sans-serif" }}>
      <div className="grain-overlay" />
      <LandingNavbar scrolled={scrolled} />

      {/* ============================================================ */}
      {/*  1. HERO                                                      */}
      {/* ============================================================ */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col justify-center pt-28 lg:pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full bg-[#0F6E56]/[0.04] blur-[120px]" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12 w-full">
          {/* Grid: ديسكتوب = النص يمين (5 أعمدة) والفيديو يسار (7 أعمدة) — جوال = النص فوق والفيديو تحت */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">

            {/* النص + CTA — يظهر أولاً دائماً */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
              className="text-center lg:text-right order-1 lg:col-span-5"
            >
              <h1 className="text-[2.2rem] sm:text-[2.8rem] lg:text-[2.6rem] xl:text-[3rem] font-bold tracking-tight mb-5">
                <div className="leading-tight">نظام مختبرك</div>
                <div className="text-[#0F6E56] leading-tight mt-2">+ موقعك الإلكتروني — في دقائق</div>
              </h1>
              <p className="text-[15px] sm:text-base leading-relaxed text-[#4A4940] max-w-[560px] mx-auto lg:mx-0 lg:max-w-none mb-7">
                أنظمة المختبرات الثانية تطلب منك ميزانية إضافية لموقع، وتأخذ أسابيع للإعداد. مع LIS Techs، نظام كامل + موقع احترافي بنطاق مخصص و SEO جاهز، +201 تحليل جاهز، وتبدأ تشتغل من اليوم — بدون انتظار فريق مبيعات.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-7">
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#0F6E56] text-white font-semibold text-[15px] hover:bg-[#0d5f4a] transition-colors shadow-lg shadow-[#0F6E56]/20"
                >
                  جرّب مجانًا — بدون بطاقة
                  <ArrowLeft className="w-4 h-4" />
                </Link>
                <a
                  href="#features"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl border border-[#E8E5DD] bg-white text-[#1A1A18] font-semibold text-[15px] hover:border-[#ccc9c0] transition-colors"
                >
                  شوف كيف يشتغل
                </a>
              </div>
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 text-[13px] text-[#8A8780]">
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#0F6E56]" /> الأسعار واضحة — بدون مفاجآت</span>
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#0F6E56]" /> +201 تحليل جاهز</span>
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#0F6E56]" /> موقع للمختبر مشمول</span>
              </div>
              <div className="mt-5 flex items-center justify-center lg:justify-start">
                <img src="/images/landing/SAUDI_MADE.svg" alt="صنع في السعودية" className="h-7 sm:h-8" />
              </div>
            </motion.div>

            {/* صورة المنتج — فريم متصفح ماك */}
            <motion.div
              style={{ y: heroParallax }}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
              className="order-2 w-full lg:col-span-7"
            >
              {/* Safari browser frame */}
              <div className="rounded-xl sm:rounded-2xl shadow-2xl shadow-black/[0.15] overflow-hidden border border-[#d1d0cb]">
                {/* Browser toolbar */}
                <div className="bg-[#f6f5f4] border-b border-[#d1d0cb] px-3 sm:px-4 py-2 sm:py-2.5 flex items-center gap-2 sm:gap-3">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[#ff5f57]" />
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[#febc2e]" />
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[#28c840]" />
                  </div>
                  <div className="flex-1 mx-2 sm:mx-8">
                    <div className="bg-white rounded-md sm:rounded-lg px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs text-[#8A8780] text-center border border-[#e4e3df] truncate">
                      listechs.com
                    </div>
                  </div>
                </div>
                {/* Dashboard screenshot */}
                <img
                  src="/images/landing/hero-dashboard.png"
                  alt="لوحة تحكم LIS Techs — نظام إدارة المختبرات"
                  className="w-full h-auto block"
                  loading="eager"
                />
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  2. SOCIAL PROOF                                              */}
      {/*       Customer logos. URLs are owner-supplied direct links to */}
      {/*       each lab's actual logo file (CDNs, Azure blobs, WP      */}
      {/*       uploads). Empty alt strings so visitors don't see a     */}
      {/*       tooltip on hover — the visual logo speaks for itself.   */}
      {/* ============================================================ */}
      <section className="py-10 sm:py-14 border-y border-[#E8E5DD] bg-[#F7F6F1]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-8 lg:px-12">
          <div className="text-center mb-6 sm:mb-8">
            <span className="text-[11px] sm:text-[12px] font-semibold tracking-[0.18em] uppercase text-[#8A8780]">
              مختبرات تستخدم LIS Techs
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-5 lg:gap-6">
            {[
              { src: 'https://trustmedlabs.com/wp-content/uploads/2025/06/footer-logo-1.png' },
              { src: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTbGZJNtbAbq9XtT7-fUIOyYJz6O4EXYlHjcA&s' },
              { src: 'https://alfalaboratory.com/wp-content/uploads/2024/08/new_logo.svg' },
              { src: 'https://medicalentitystorageprod.blob.core.windows.net/501610/Profile/passport_5fd30992-e670-4b6c-8534-c66eb1af4d0c.jpg?sv=2017-04-17&sr=b&si=PrivatePolicy&sig=Kg%2FGOpPTsT6c3nxPcJ7U1qMbZaYQSMpwY%2B2CGGH1l88%3D' },
            ].map((lab) => (
              <div
                key={lab.src}
                className="bg-white rounded-xl px-4 sm:px-5 py-3 border border-[#E8E5DD]/60 hover:shadow-sm transition-all flex items-center justify-center min-h-[60px] sm:min-h-[68px] min-w-[120px] sm:min-w-[140px]"
              >
                <img
                  src={lab.src}
                  alt=""
                  className="h-8 sm:h-9 md:h-10 max-w-[140px] object-contain"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  3. FEATURES — BENTO GRID                                    */}
      {/* ============================================================ */}
      <section id="features" className="py-14 sm:py-20 lg:py-28 bg-[#F7F6F1]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-8 lg:px-12">
          <RevealSection>
            <div className="text-center mb-8 sm:mb-14">
              <h2 className="text-[1.5rem] sm:text-[2rem] lg:text-[3rem] font-bold leading-tight mb-3 sm:mb-4">
                ليش المختبرات تنتقل إلى <span className="text-[#0F6E56]">LIS Techs</span>
              </h2>
              <p className="text-[13px] sm:text-[15px] text-[#4A4940] max-w-[520px] mx-auto leading-relaxed">
                كل ميزة بُنيت لأننا شفنا مشكلة حقيقية في الأنظمة الموجودة — وقررنا نحلها صح.
              </p>
            </div>
          </RevealSection>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
            {FEATURES.map((feat, i) => {
              const Icon = feat.icon;
              const isWide = i === 0 || feat.image;
              return (
                <RevealSection key={i} delay={i * 0.05} className={isWide ? 'col-span-2' : ''}>
                  <div className={`group bg-white rounded-xl sm:rounded-2xl border border-[#E8E5DD] h-full hover:border-[#ccc9c0] hover:shadow-sm transition-all duration-300 overflow-hidden ${feat.image ? 'sm:flex sm:flex-row-reverse' : ''}`}>
                    {/* صورة الخاصية (إن وجدت) */}
                    {feat.image && (
                      <div className="sm:w-[45%] lg:w-[50%] shrink-0 bg-[#F7F6F1] border-b sm:border-b-0 sm:border-r border-[#E8E5DD]">
                        <img src={feat.image} alt={feat.title} className="w-full h-full object-cover object-top" loading="lazy" />
                      </div>
                    )}
                    <div className={`p-4 sm:p-6 lg:p-7 ${isWide && !feat.image ? 'sm:flex sm:items-start sm:gap-5 lg:gap-8' : ''} ${feat.image ? 'flex-1 flex flex-col justify-center' : ''}`}>
                      <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-[#F7F6F1] group-hover:bg-[#0F6E56]/[0.08] flex items-center justify-center mb-2 sm:mb-4 transition-colors ${isWide && !feat.image ? 'sm:mb-0 sm:shrink-0 lg:w-14 lg:h-14' : ''}`}>
                        <Icon className={`w-4 h-4 sm:w-5 sm:h-5 text-[#0F6E56] ${isWide ? 'lg:w-6 lg:h-6' : ''}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
                          <h3 className={`font-bold ${isWide ? 'text-[13px] sm:text-lg' : 'text-[12px] sm:text-[15px]'}`}>{feat.title}</h3>
                          {feat.tag && (
                            <span className="text-[9px] sm:text-[11px] text-[#8A8780] bg-[#F7F6F1] px-1.5 sm:px-2 py-0.5 rounded-full">{feat.tag}</span>
                          )}
                        </div>
                        <p className={`text-[#4A4940] leading-relaxed ${isWide ? 'text-[11px] sm:text-[15px] max-w-md' : 'text-[11px] sm:text-[13px]'}`}>{feat.desc}</p>
                      </div>
                    </div>
                  </div>
                </RevealSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  5. HOW IT WORKS                                              */}
      {/* ============================================================ */}
      <section className="py-14 sm:py-20 lg:py-28 bg-[#FDFCF9]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-8 lg:px-12">
          <RevealSection>
            <div className="text-center mb-8 sm:mb-16">
              <h2 className="text-[1.5rem] sm:text-[2rem] lg:text-[2.5rem] font-bold leading-tight mb-3 sm:mb-4">
                ثلاث خطوات وتبدأ. <span className="text-[#0F6E56]">بدون انتظار أحد.</span>
              </h2>
              <p className="text-[13px] sm:text-[15px] text-[#4A4940] max-w-[480px] mx-auto leading-relaxed">
                بدون فريق مبيعات، بدون إعداد يأخذ أسابيع، بدون إدخال يدوي. سجّل وابدأ.
              </p>
            </div>
          </RevealSection>

          <div className="relative grid grid-cols-3 gap-4 sm:gap-8 lg:gap-12">
            <div className="hidden sm:block absolute top-[44px] right-[16.5%] left-[16.5%] h-[2px] bg-[#E8E5DD]" />
            {STEPS.map((step, i) => (
              <RevealSection key={i} delay={i * 0.12}>
                <div className="text-center relative">
                  <div className="relative inline-flex items-center justify-center w-14 h-14 sm:w-[88px] sm:h-[88px] rounded-full border-2 border-[#E8E5DD] bg-white mb-3 sm:mb-6 z-10">
                    <span className="text-base sm:text-2xl font-bold text-[#0F6E56]">{step.num}</span>
                  </div>
                  <h3 className="text-[12px] sm:text-lg font-bold mb-1 sm:mb-2">{step.title}</h3>
                  <p className="text-[10px] sm:text-[14px] text-[#4A4940] leading-relaxed max-w-[280px] mx-auto">{step.desc}</p>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  5b. WEBSITE BUILDER — مشمول مع الاشتراك                       */}
      {/*       Showcases the 3-template gallery + custom domain + SEO   */}
      {/*       + visual editor as a flagship value-add.                 */}
      {/* ============================================================ */}
      <section id="website-builder" className="py-14 sm:py-20 lg:py-32 bg-[#FDFCF9] relative overflow-hidden">
        {/* Soft brand glow + subtle grid pattern to set this section apart */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 right-1/2 translate-x-1/2 w-[900px] h-[600px] rounded-full bg-[#0F6E56]/[0.05] blur-[120px]" />
        </div>

        <div className="relative max-w-[1280px] mx-auto px-4 sm:px-8 lg:px-12">
          {/* Header */}
          <RevealSection>
            <div className="text-center mb-10 sm:mb-14 lg:mb-16 max-w-[760px] mx-auto">
              <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-[#0F6E56]/8 border border-[#0F6E56]/15">
                <Sparkles className="w-3.5 h-3.5 text-[#0F6E56]" />
                <span className="text-[11px] sm:text-[12px] font-bold tracking-wider text-[#0F6E56]">مع الباقة المتقدمة والاحترافية</span>
              </div>
              <h2 className="text-[1.6rem] sm:text-[2.2rem] lg:text-[3rem] font-bold leading-tight mb-4 sm:mb-5">
                مختبرك يستحق <span className="text-[#0F6E56]">موقع احترافي</span>
                <br className="hidden sm:block" />
                — جاهز من اليوم الأول
              </h2>
              <p className="text-[14px] sm:text-[16px] text-[#4A4940] leading-relaxed max-w-[640px] mx-auto">
                موقع إلكتروني متكامل لمختبرك مدمج في النظام: <strong className="text-[#1A1A18]">نطاق مخصص</strong>،
                <strong className="text-[#1A1A18]"> SEO جاهز</strong>،
                <strong className="text-[#1A1A18]"> قوالب احترافية متعددة</strong> تختار منها،
                و<strong className="text-[#1A1A18]">محرر مرئي</strong> تعدّل فيه كل شيء بدون مطوّر — بدون تكلفة موقع منفصلة.
              </p>
            </div>
          </RevealSection>

          {/* Capabilities grid — 8 features that prove "complete website" */}
          <RevealSection delay={0.15}>
            <div className="bg-white rounded-2xl border border-[#E8E5DD] p-5 sm:p-8 lg:p-10 mb-10 sm:mb-12">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 sm:gap-x-8 gap-y-6 sm:gap-y-8">
                {[
                  { icon: Globe,             title: 'نطاق مخصص',         desc: 'نطاق فرعي مجاني، أو اربط نطاقك (lab.com)' },
                  { icon: Search,            title: 'SEO جاهز',          desc: 'sitemap + robots + Schema تلقائي' },
                  { icon: MousePointerClick, title: 'محرر مرئي',         desc: 'انقر أي نص لتعديله — بدون كود' },
                  { icon: Languages,         title: 'ثنائي اللغة',       desc: 'عربي وإنجليزي — مع اتجاه RTL/LTR' },
                  { icon: Smartphone,        title: 'متجاوب 100%',       desc: 'يعمل بسلاسة على الموبايل والتابلت' },
                  { icon: CalendarCheck,     title: 'حجز مباشر',         desc: 'يربط بنظام المواعيد فوراً' },
                  { icon: BrainCircuit,      title: 'محتوى ذكي',         desc: 'صياغة احترافية بالذكاء الاصطناعي' },
                  { icon: Layout,            title: 'بلوكات قابلة للتخصيص', desc: 'أضِف أقسام، رتّب، أخفِ — كما تريد' },
                ].map((cap, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#F7F6F1] flex items-center justify-center shrink-0">
                      <cap.icon className="w-5 h-5 text-[#0F6E56]" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-[13px] sm:text-[14px] font-bold text-[#1A1A18] mb-1">{cap.title}</h4>
                      <p className="text-[11px] sm:text-[12px] text-[#8A8780] leading-relaxed">{cap.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </RevealSection>

          {/* Bottom strip — proof + CTA */}
          <RevealSection delay={0.2}>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6 px-1 sm:px-2">
              <div className="flex items-center gap-3 text-center sm:text-right">
                <div className="hidden sm:flex w-10 h-10 rounded-full bg-[#0F6E56]/10 items-center justify-center shrink-0">
                  <Check className="w-5 h-5 text-[#0F6E56]" />
                </div>
                <div>
                  <p className="text-[13px] sm:text-[14px] font-bold text-[#1A1A18] mb-0.5">
                    أنشئ موقعك في أقل من 10 دقائق
                  </p>
                  <p className="text-[11px] sm:text-[12px] text-[#8A8780]">
                    اختر القالب، حدّث المحتوى، انشر. كل شي من نفس لوحة التحكم.
                  </p>
                </div>
              </div>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-6 sm:px-7 py-3 sm:py-3.5 rounded-xl bg-[#0F6E56] text-white font-semibold text-[14px] sm:text-[15px] hover:bg-[#0d5f4a] transition-colors shadow-lg shadow-[#0F6E56]/20 whitespace-nowrap"
              >
                ابدأ موقعك مجاناً
                <ArrowLeft className="w-4 h-4" />
              </Link>
            </div>
          </RevealSection>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  6. PATIENT PORTAL + AI                                        */}
      {/* ============================================================ */}
      <section className="py-14 sm:py-20 lg:py-28 bg-[#0F3D30] text-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 50% 0%, #1D9E75 0%, transparent 60%)' }} />
        <div className="relative max-w-[1280px] mx-auto px-4 sm:px-8 lg:px-12">
          <RevealSection>
            <div className="text-center mb-8 sm:mb-14">
              <SectionLabel text="بوابة المرضى" />
              <h2 className="text-[1.5rem] sm:text-[2rem] lg:text-[2.5rem] font-bold leading-tight mb-3 sm:mb-4">
                مرضاك يشوفون نتائجهم <span className="text-emerald-400">مع تفسير ذكي</span>
              </h2>
              <p className="text-[13px] sm:text-[15px] text-emerald-100/70 max-w-[520px] mx-auto leading-relaxed">
                بوابة مخصصة لكل مختبر — المريض يدخل ويشوف نتائجه مع تحليل بالذكاء الاصطناعي يفهّمه تحاليله بلغة بسيطة.
              </p>
            </div>
          </RevealSection>

          {/* 3 iPhone mockups side by side */}
          <RevealSection delay={0.15}>
            <div className="flex items-end justify-center gap-3 sm:gap-5 lg:gap-8 max-w-[720px] mx-auto">
              {[
                { src: '/images/landing/patient-portal-home.png', alt: 'بوابة المرضى', label: 'بوابة المريض', active: false },
                { src: '/images/landing/patient-results.png', alt: 'نتائج التحاليل', label: 'نتائج التحاليل', active: true },
                { src: '/images/landing/patient-ai-mobile.png', alt: 'تحليل AI ذكي', label: 'تحليل AI ذكي', active: false },
              ].map((phone, i) => (
                <div key={i} className={`${phone.active ? 'w-[36%] sm:w-[34%] -mb-2' : 'w-[30%] sm:w-[28%]'}`}>
                  {/* iPhone Frame */}
                  <div className={`relative bg-[#1a1a1a] rounded-[16px] sm:rounded-[24px] lg:rounded-[32px] p-[3px] sm:p-[4px] shadow-2xl ${phone.active ? 'shadow-emerald-900/40 ring-1 ring-emerald-400/20' : 'shadow-black/30'}`}>
                    {/* Notch / Dynamic Island */}
                    <div className="absolute top-[6px] sm:top-[8px] left-1/2 -translate-x-1/2 w-[30%] sm:w-[28%] h-[6px] sm:h-[8px] bg-[#1a1a1a] rounded-full z-10" />
                    {/* Screen */}
                    <div className="rounded-[13px] sm:rounded-[20px] lg:rounded-[28px] overflow-hidden bg-white">
                      <img src={phone.src} alt={phone.alt} className="w-full h-auto block" loading="lazy" />
                    </div>
                    {/* Home indicator */}
                    <div className="absolute bottom-[4px] sm:bottom-[6px] left-1/2 -translate-x-1/2 w-[32%] h-[3px] sm:h-[4px] bg-white/30 rounded-full" />
                  </div>
                  <p className={`text-center mt-2 sm:mt-3 ${phone.active ? 'text-[10px] sm:text-xs text-emerald-300/70 font-medium' : 'text-[10px] sm:text-xs text-emerald-200/50'}`}>{phone.label}</p>
                </div>
              ))}
            </div>
          </RevealSection>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  7. FOUNDER STORY                                             */}
      {/* ============================================================ */}
      <section className="py-14 sm:py-20 lg:py-28 bg-[#F7F6F1]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-8 lg:px-12">
          <RevealSection>
            <div className="max-w-[720px] mx-auto text-center">
              <SectionLabel text="ليش بنينا LIS Techs" />
              <Quote className="w-8 h-8 sm:w-10 sm:h-10 text-[#0F6E56]/20 mx-auto mb-4 sm:mb-6" />
              <blockquote className="text-base sm:text-xl lg:text-2xl font-bold leading-relaxed mb-6 sm:mb-8 text-[#1A1A18]">
                "اشتغلنا مع مختبرات تستخدم أنظمة مختلفة — وشفنا نفس المشاكل في كل مكان: أسعار مخفية، أسابيع إعداد، واجهات معقدة، وبيانات تحاليل لازم تدخلها من الصفر. قلنا لازم نبني نظام يخلّيك تسجّل وتبدأ — بدون ما تنتظر أحد."
              </blockquote>
              <div className="flex items-center justify-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#0F6E56]/10 flex items-center justify-center">
                  <span className="text-[15px] font-bold text-[#0F6E56]">م</span>
                </div>
                <div className="text-right">
                  <div className="text-[15px] font-bold text-[#1A1A18]">فريق LIS Techs</div>
                  <div className="text-[13px] text-[#8A8780]">بنيناه لأننا شفنا المشكلة في كل مختبر تعاملنا معه</div>
                </div>
              </div>
            </div>
          </RevealSection>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  9. TRUST / SECURITY                                          */}
      {/* ============================================================ */}
      <section className="py-14 sm:py-20 lg:py-28 bg-[#FDFCF9]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-8 lg:px-12">
          <RevealSection>
            <div className="text-center mb-8 sm:mb-14">
              <h2 className="text-[1.5rem] sm:text-[2rem] lg:text-[2.5rem] font-bold leading-tight mb-3 sm:mb-4">
                بياناتك ما تطلع من <span className="text-[#0F6E56]">سيرفراتك</span>
              </h2>
              <p className="text-[13px] sm:text-[15px] text-[#4A4940] max-w-[520px] mx-auto leading-relaxed">
                الذكاء الاصطناعي في LIS Techs يشتغل بنموذج محلي — بيانات المرضى ما تنرسل لأي خدمة خارجية. خصوصية كاملة بمعايير بنكية.
              </p>
            </div>
          </RevealSection>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 mb-6 sm:mb-10">
            {[
              { stat: 'محلي', label: 'AI لا يرسل بيانات خارجيًا', icon: BrainCircuit },
              { stat: 'AES-256', label: 'تشفير بنكي', icon: Lock },
              { stat: 'يومي', label: 'نسخ احتياطية', icon: Server },
              { stat: '99.9%', label: 'وقت التشغيل', icon: Clock },
            ].map((item, i) => (
              <RevealSection key={i} delay={i * 0.08}>
                <div className="bg-white rounded-xl sm:rounded-2xl border border-[#E8E5DD] p-4 sm:p-6 text-center hover:border-[#ccc9c0] hover:shadow-sm transition-all duration-300">
                  <item.icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#0F6E56] mx-auto mb-2 sm:mb-3" />
                  <div className="text-lg sm:text-2xl font-bold mb-0.5 sm:mb-1">{item.stat}</div>
                  <div className="text-[11px] sm:text-[13px] text-[#8A8780]">{item.label}</div>
                </div>
              </RevealSection>
            ))}
          </div>

          <RevealSection delay={0.2}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 max-w-[640px] mx-auto">
              {[
                'نموذج AI محلي — بيانات المرضى ما تطلع من سيرفراتك أبدًا',
                'تشفير SSL/TLS لكل بايت ينتقل',
                'صلاحيات متعددة المستويات — كل موظف يشوف بس شغله',
                'سجل تدقيق كامل — تعرف مين سوى إيش ومتى',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#0F6E56] shrink-0" />
                  <span className="text-[12px] sm:text-[14px] text-[#4A4940]">{item}</span>
                </div>
              ))}
            </div>
          </RevealSection>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  FAQ                                                          */}
      {/* ============================================================ */}
      <section id="faq" className="py-14 sm:py-20 lg:py-28 bg-[#FDFCF9]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-8 lg:px-12">
          <RevealSection>
            <div className="text-center mb-8 sm:mb-14">
              <h2 className="text-[1.5rem] sm:text-[2rem] lg:text-[2.5rem] font-bold leading-tight mb-3 sm:mb-4">
                أسئلة قبل ما تقرر؟
              </h2>
              <p className="text-[13px] sm:text-[15px] text-[#4A4940] max-w-[480px] mx-auto leading-relaxed">
                جمعنا أكثر الأسئلة اللي يسألها أصحاب المختبرات. ما لقيت جوابك؟ كلّمنا مباشرة.
              </p>
            </div>
          </RevealSection>

          <div className="max-w-[720px] mx-auto space-y-3">
            {FAQS.map((faq, i) => {
              const isOpen = openFaq === i;
              return (
                <RevealSection key={i} delay={i * 0.05}>
                  <div className="bg-white rounded-xl border border-[#E8E5DD] overflow-hidden transition-colors hover:border-[#ccc9c0]">
                    <button
                      onClick={() => setOpenFaq(isOpen ? null : i)}
                      className="w-full flex items-center justify-between gap-4 px-6 py-5 text-right"
                    >
                      <span className="text-[15px] font-semibold text-[#1A1A18] leading-relaxed">{faq.q}</span>
                      <motion.div
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.25 }}
                        className="shrink-0"
                      >
                        <ChevronDown className="w-5 h-5 text-[#8A8780]" />
                      </motion.div>
                    </button>
                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                        >
                          <div className="px-6 pb-5 text-[14px] text-[#4A4940] leading-relaxed border-t border-[#E8E5DD] pt-4">
                            {faq.a}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </RevealSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  12. FINAL CTA                                                */}
      {/* ============================================================ */}
      <section className="py-14 sm:py-20 lg:py-28 bg-[#0F6E56] text-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full bg-white/[0.05] blur-[120px]" />
        </div>
        <div className="relative max-w-[1280px] mx-auto px-4 sm:px-8 lg:px-12 text-center">
          <RevealSection>
            <h2 className="text-[1.5rem] sm:text-[2rem] lg:text-[3rem] font-bold leading-tight mb-4 sm:mb-5">
              زملائك بدأوا — متى تبدأ أنت؟
            </h2>
            <p className="text-emerald-100/80 text-[13px] sm:text-[15px] max-w-[480px] mx-auto leading-relaxed mb-8 sm:mb-10">
              سجّل في دقائق، فعّل قوالبك، وابدأ تشتغل. بدون انتظار فريق مبيعات، بدون إعداد يأخذ أسابيع. التجربة مجانية.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-white text-[#0F6E56] font-semibold text-[15px] hover:bg-emerald-50 transition-colors shadow-lg shadow-black/10"
              >
                ابدأ مجانًا — الأسعار واضحة
                <ArrowLeft className="w-4 h-4" />
              </Link>
              <a
                href="mailto:support@listechs.sa"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl border border-white/30 text-white font-semibold text-[15px] hover:bg-white/10 transition-colors"
              >
                كلّمنا — نجاوبك فورًا
              </a>
            </div>
          </RevealSection>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  13. FOOTER                                                   */}
      {/* ============================================================ */}
      <footer className="bg-[#1A1A18] text-white py-10 sm:py-16">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-8 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-10 mb-8 sm:mb-12">
            <div>
              <div className="mb-4"><img src="/listech-logo.svg" alt="LIS Techs" style={{ height: 36, filter: 'brightness(0) invert(1)', opacity: 0.9 }} /></div>
              <p className="text-[14px] text-white/50 leading-relaxed mb-6 max-w-[260px]">
                منصة سعودية متكاملة لإدارة المختبرات الطبية. نساعدك على تشغيل مختبرك بكفاءة عالية.
              </p>
              <div className="flex items-center gap-3">
                <a href="https://twitter.com/listechssa" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/15 flex items-center justify-center transition-colors">
                  <Twitter className="w-4 h-4" />
                </a>
                <a href="https://linkedin.com/company/listechs" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/15 flex items-center justify-center transition-colors">
                  <Linkedin className="w-4 h-4" />
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-[14px] font-semibold mb-4 text-white/80">المنتج</h4>
              <ul className="space-y-2.5">
                <li><a href="#features" className="text-[14px] text-white/50 hover:text-white/80 transition-colors">المميزات</a></li>
                <li><a href="#website-builder" className="text-[14px] text-white/50 hover:text-white/80 transition-colors">موقع إلكتروني للمختبر</a></li>
                <li><a href="#pricing" className="text-[14px] text-white/50 hover:text-white/80 transition-colors">الأسعار</a></li>
                <li><a href="#faq" className="text-[14px] text-white/50 hover:text-white/80 transition-colors">الأسئلة الشائعة</a></li>
                <li><Link to="/register" className="text-[14px] text-white/50 hover:text-white/80 transition-colors">تجربة مجانية</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-[14px] font-semibold mb-4 text-white/80">الشركة</h4>
              <ul className="space-y-2.5">
                <li><a href="#" className="text-[14px] text-white/50 hover:text-white/80 transition-colors">عن LIS Techs</a></li>
                <li><a href="#" className="text-[14px] text-white/50 hover:text-white/80 transition-colors">سياسة الخصوصية</a></li>
                <li><a href="#" className="text-[14px] text-white/50 hover:text-white/80 transition-colors">الشروط والأحكام</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-[14px] font-semibold mb-4 text-white/80">تواصل معنا</h4>
              <ul className="space-y-3">
                <li className="flex items-center gap-2.5">
                  <Mail className="w-4 h-4 text-white/40" />
                  <a href="mailto:support@listechs.sa" className="text-[14px] text-white/50 hover:text-white/80 transition-colors">support@listechs.sa</a>
                </li>
                <li className="flex items-center gap-2.5">
                  <Phone className="w-4 h-4 text-white/40" />
                  <a href="tel:+966500000000" className="text-[14px] text-white/50 hover:text-white/80 transition-colors" dir="ltr">+966 50 000 0000</a>
                </li>
                <li className="flex items-center gap-2.5">
                  <MapPin className="w-4 h-4 text-white/40" />
                  <span className="text-[14px] text-white/50">الرياض، المملكة العربية السعودية</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="text-[13px] text-white/40">&copy; {new Date().getFullYear()} LIS Techs. جميع الحقوق محفوظة.</span>
            <span className="flex items-center gap-2 text-[13px] text-white/30">
              <img src="/images/landing/SAUDI_MADE.svg" alt="صنع في السعودية" className="h-5 opacity-40 invert" />
              صنع بعناية في السعودية
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
